import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';

export type BadgeTone =
  | 'duration'
  | 'reading'
  | 'writing'
  | 'math'
  | 'done'
  | 'in-progress'
  | 'not-started'
  | 'redo'
  | 'neutral';

const TONE: Record<BadgeTone, string> = {
  // Figma badge: every duration badge is the same dark teal, whatever the exercise.
  duration: 'bg-badge-duration text-on-action-primary',
  reading: 'bg-exercise-reading text-on-action-primary',
  writing: 'bg-exercise-writing text-on-action-primary',
  math: 'bg-exercise-math text-on-action-primary',
  // Figma task-list/items: the row states — 14px bold on a 20px radius.
  done: 'bg-badge-done text-on-action-primary rounded-xl',
  'in-progress': 'bg-badge-in-progress text-on-badge-in-progress rounded-xl',
  'not-started': 'bg-surface-tertiary text-on-surface-medium rounded-xl',
  redo: 'bg-attention text-on-attention',
  neutral: 'bg-surface-tertiary text-on-surface-medium',
};

const STATE_TONES: ReadonlySet<BadgeTone> = new Set(['done', 'in-progress', 'not-started']);

/**
 * DESIGN.md › badge: caption-sm pill — dark teal for durations, teal/orange for Done/Redo.
 * The task-list states read in `body-xs` bold and may carry a trailing 16px glyph
 * (check, timer), sized and coloured by the caller.
 */
export function Badge({
  tone = 'neutral',
  icon,
  className,
  children,
  ...props
}: HTMLAttributes<HTMLSpanElement> & { tone?: BadgeTone; icon?: ReactNode }) {
  const state = STATE_TONES.has(tone);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-internal-sm rounded-full px-internal-md py-internal-sm font-brand',
        state ? 'text-body-xs font-bold' : 'text-caption-sm',
        TONE[tone],
        className,
      )}
      {...props}
    >
      {children}
      {icon}
    </span>
  );
}
